import { ScreenerFilter } from '@toggle/toggle';
import { TFunction } from 'i18next';

import {
  getCustomIndexMoves,
  getCustomInflation,
  getCustomInterest,
  getCustomOil,
} from './custom-scenario-conditions-filters';

// eslint-disable-next-line max-lines-per-function
export const getConditionFilters = (t: TFunction): ScreenerFilter[] => [
  {
    key: 'central_bank_actions',
    name: t('scenario:filters.conditions', {
      context: 'central_bank_actions',
    }),
    visible: true,
    searchable: false,
    category: 'Scenario',
    locked: true,
    dynamic: true,
    config: {
      multiselect: false,
      name: t('scenario:filters.centralBankFilter.centralBank'),
      options: [
        {
          name: t('scenario:filters.centralBankFilter.hikes'),
          key: 'hikes',
          value: {
            snake: '_hike',
            condition: 'crosses_above',
            value: 0.5,
          },
        },
        {
          name: t('scenario:filters.centralBankFilter.cuts'),
          key: 'cuts',
          value: {
            snake: '_cut',
            condition: 'crosses_above',
            value: 0.5,
          },
        },
      ],
      children: [
        {
          name: t('scenario:filters.centralBankFilter.centralBank'),
          multiselect: false,
          options: [
            {
              name: t('scenario:filters.centralBankFilter.fed'),
              key: 'fed',
              value: {
                value: 'fed.rate',
              },
            },
            {
              name: t('scenario:filters.centralBankFilter.boe'),
              key: 'boe',
              value: {
                value: 'boe.rate',
              },
            },
            {
              name: t('scenario:filters.centralBankFilter.boj'),
              key: 'boj',
              value: {
                value: 'boj.rate',
              },
            },
            {
              name: t('scenario:filters.centralBankFilter.ecb'),
              key: 'ecb',
              value: {
                value: 'ecb.rate',
              },
            },
          ],
          default: 'fed',
        },
      ],
    },
  },
  {
    key: 'oil-prices',
    name: t('scenario:filters.conditions', {
      context: 'oil-prices',
    }),
    visible: true,
    searchable: false,
    category: 'Scenario',
    locked: true,
    dynamic: true,
    config: {
      multiselect: false,
      name: t('scenario:filters.oilPricesFilter.oilPrice'),
      options: [
        {
          name: t('scenario:filters.oilPricesFilter.priceRises10Week'),
          key: 'rises_10_w',
          value: {
            condition: 'rises_by',
            value: 0.1,
            duration: 1,
            duration_unit: 'w',
          },
        },
        {
          name: t('scenario:filters.oilPricesFilter.priceRises10Month'),
          key: 'rises_10_m',
          value: {
            condition: 'rises_by',
            value: 0.1,
            duration: 1,
            duration_unit: 'm',
          },
        },
        {
          name: t('scenario:filters.oilPricesFilter.priceFalls10Week'),
          key: 'falls_10_w',
          value: {
            condition: 'falls_by',
            value: 0.1,
            duration: 1,
            duration_unit: 'w',
          },
        },
        {
          name: t('scenario:filters.oilPricesFilter.priceFalls10Month'),
          key: 'falls_10_m',
          value: {
            condition: 'falls_by',
            value: 0.1,
            duration: 1,
            duration_unit: 'm',
          },
        },
      ],
      children: [
        {
          name: t('scenario:filters.oilPricesFilter.instrumentSelector'),
          multiselect: false,
          options: [
            {
              name: t('scenario:filters.oilPricesFilter.coIce'),
              key: 'co_ice',
              description: t('scenario:filters.oilPricesFilter.brentCrude'),
              value: {
                value: 'co_ice_fut_energy.price',
              },
            },
            {
              name: t('scenario:filters.oilPricesFilter.clNymex'),
              key: 'cl_nymex',
              description: t(
                'scenario:filters.oilPricesFilter.lightSweetCrudeOil'
              ),
              value: {
                value: 'cl_nymex_fut_energy.price',
              },
            },
            {
              name: t('scenario:filters.oilPricesFilter.usoArca'),
              key: 'use_arca',
              description: t('scenario:filters.oilPricesFilter.usOilFund'),
              value: {
                value: 'uso_arca.price',
              },
            },
          ],
          default: 'co_ice',
        },
      ],
      custom: getCustomOil(t),
    },
  },
  {
    key: 'interest-rate',
    name: t('scenario:filters.conditions', {
      context: 'interest-rate',
    }),
    visible: true,
    searchable: false,
    category: 'Scenario',
    locked: true,
    dynamic: true,
    config: {
      name: t('scenario:filters.interestRateFilter.interestRate'),
      multiselect: false,
      options: [
        {
          name: t('scenario:filters.interestRateFilter.yieldRises10bpsWeek'),
          key: 'rises_10bps_w',
          value: {
            snake: '.yield',
            condition: 'rises_by_level',
            value: 0.1,
            duration: 1,
            duration_unit: 'w',
          },
        },
        {
          name: t('scenario:filters.interestRateFilter.yieldFalls10bpsWeek'),
          key: 'falls_10_m',
          value: {
            snake: '.yield',
            condition: 'falls_by_level',
            value: 0.1,
            duration: 1,
            duration_unit: 'w',
          },
        },
        {
          name: t('scenario:filters.interestRateFilter.yieldCrossesAbove4'),
          key: 'crosses_above_4',
          value: {
            snake: '.yield',
            condition: 'crosses_above',
            value: 4,
          },
        },
        {
          name: t('scenario:filters.interestRateFilter.yieldCrossesBelow4'),
          key: 'crosses_below_4',
          value: {
            snake: '.yield',
            condition: 'crosses_below',
            value: 4,
          },
        },
      ],
      children: [
        {
          name: t('scenario:filters.interestRateFilter.country'),
          multiselect: false,
          options: [
            {
              name: t('scenario:filters.interestRateFilter.us'),
              key: 'us',
              value: {
                value: 'sov_us_usd_',
              },
            },
            {
              name: t('scenario:filters.interestRateFilter.uk'),
              key: 'uk',
              value: {
                value: 'sov_uk_gbp_',
              },
            },
            {
              name: t('scenario:filters.interestRateFilter.japan'),
              key: 'jpn',
              value: {
                value: 'sov_jpn_jpy_',
              },
            },
            {
              name: t('scenario:filters.interestRateFilter.germany'),
              key: 'deu',
              value: {
                value: 'sov_deu_eur_',
              },
            },
          ],
          default: 'us',
        },
        {
          name: t('scenario:filters.interestRateFilter.tenor'),
          multiselect: false,
          options: [
            {
              name: t('scenario:filters.interestRateFilter.tenor2y'),
              key: '2y',
              value: {
                value: '2y',
              },
            },
            {
              name: t('scenario:filters.interestRateFilter.tenor3y'),
              key: '3y',
              value: {
                value: '3y',
              },
            },
            {
              name: t('scenario:filters.interestRateFilter.tenor5y'),
              key: '5y',
              value: {
                value: '5y',
              },
            },
            {
              name: t('scenario:filters.interestRateFilter.tenor7y'),
              key: '7y',
              value: {
                value: '7y',
              },
            },
            {
              name: t('scenario:filters.interestRateFilter.tenor10y'),
              key: '10y',
              value: {
                value: '10y',
              },
            },
            {
              name: t('scenario:filters.interestRateFilter.tenor30y'),
              key: '30y',
              value: {
                value: '30y',
              },
            },
          ],
          default: '2y',
        },
      ],
      custom: getCustomInterest(t),
    },
  },
  {
    key: 'rate-curve',
    name: t('scenario:filters.conditions', {
      context: 'rate-curve',
    }),
    visible: true,
    searchable: false,
    category: 'Scenario',
    locked: true,
    dynamic: true,
    config: {
      name: t('scenario:filters.rateCurveFilter.rateCurve'),
      multiselect: false,
      options: [
        {
          name: t('scenario:filters.rateCurveFilter.rateCurveSteepens'),
          key: 'steepens',
          value: {
            snake: '.yield',
            condition: 'rises_by_level',
            value: 0.25,
            duration: 1,
            duration_unit: 'm',
          },
        },
        {
          name: t('scenario:filters.rateCurveFilter.rateCurveFlattens'),
          key: 'flattens',
          value: {
            snake: '.yield',
            condition: 'falls_by_level',
            value: 0.25,
            duration: 1,
            duration_unit: 'm',
          },
        },
        {
          name: t('scenario:filters.rateCurveFilter.rateCurveInverts'),
          key: 'inverts',
          value: {
            snake: '.yield',
            condition: 'crosses_below',
            value: 0,
          },
        },
        {
          name: t('scenario:filters.rateCurveFilter.rateCurveUninverts'),
          key: 'un_inverts',
          value: {
            snake: '.yield',
            condition: 'crosses_above',
            value: 0,
          },
        },
        {
          name: t('scenario:filters.rateCurveFilter.rateCurveIsInverted'),
          key: 'is_inverted',
          value: {
            snake: '.yield',
            condition: 'below',
            value: 0,
          },
        },
        {
          name: t('scenario:filters.rateCurveFilter.rateCurveIsUpwardSloping'),
          key: 'is_upward_sloping',
          value: {
            snake: '.yield',
            condition: 'above',
            value: 0,
          },
        },
      ],
      children: [
        {
          name: t('scenario:filters.rateCurveFilter.country'),
          multiselect: false,
          options: [
            {
              name: t('scenario:filters.rateCurveFilter.us'),
              key: 'us',
              value: {
                value: 'sov_us_usd_',
              },
            },
            {
              name: t('scenario:filters.rateCurveFilter.uk'),
              key: 'uk',
              value: {
                value: 'sov_uk_gbp_',
              },
            },
            {
              name: t('scenario:filters.rateCurveFilter.japan'),
              key: 'jpn',
              value: {
                value: 'sov_jpn_jpy_',
              },
            },
            {
              name: t('scenario:filters.rateCurveFilter.germany'),
              key: 'deu',
              value: {
                value: 'sov_deu_eur_',
              },
            },
          ],
          default: 'us',
        },
        {
          name: t('scenario:filters.rateCurveFilter.tenor'),
          multiselect: false,
          options: [
            {
              name: t('scenario:filters.rateCurveFilter.curve2s5s'),
              key: '2s5s',
              value: {
                value: '2y_5y_curve',
              },
            },
            {
              name: t('scenario:filters.rateCurveFilter.curve2s10s'),
              key: '2s10s',
              value: {
                value: '2y_10y_curve',
              },
            },
            {
              name: t('scenario:filters.rateCurveFilter.curve5s10s'),
              key: '5s10s',
              value: {
                value: '5y_10y_curve',
              },
            },
            {
              name: t('scenario:filters.rateCurveFilter.curve10s30s'),
              key: '10s30s',
              value: {
                value: '10y_30y_curve',
              },
            },
          ],
          default: '2s5s',
        },
      ],
    },
  },
  {
    key: 'inflation',
    name: t('scenario:filters.conditions', {
      context: 'inflation',
    }),
    visible: true,
    searchable: false,
    category: 'Scenario',
    locked: true,
    dynamic: true,
    config: {
      name: t('scenario:filters.inflationFilter.inflation'),
      multiselect: false,
      options: [
        {
          name: t('scenario:filters.inflationFilter.isAbove2'),
          key: 'is_above_2',
          value: {
            condition: 'above',
            value: 2,
          },
        },
        {
          name: t('scenario:filters.inflationFilter.isAbove5'),
          key: 'is_above_5',
          value: {
            condition: 'above',
            value: 5,
          },
        },
        {
          name: t('scenario:filters.inflationFilter.isBelow2'),
          key: 'is_below_2',
          value: {
            condition: 'below',
            value: 2,
          },
        },
        {
          name: t('scenario:filters.inflationFilter.isBelow5'),
          key: 'is_below_5',
          value: {
            condition: 'below',
            value: 5,
          },
        },
      ],
      children: [
        {
          name: t('scenario:filters.inflationFilter.country'),
          multiselect: false,
          options: [
            {
              name: t('scenario:filters.inflationFilter.us'),
              key: 'us',
              value: {
                value: 'us',
              },
            },
            {
              name: t('scenario:filters.inflationFilter.uk'),
              key: 'uk',
              value: {
                value: 'uk',
              },
            },
            {
              name: t('scenario:filters.inflationFilter.japan'),
              key: 'jpn',
              value: {
                value: 'jpn',
              },
            },
            {
              name: t('scenario:filters.inflationFilter.germany'),
              key: 'deu',
              value: {
                value: 'deu',
              },
            },
          ],
          default: 'us',
        },
        {
          name: t('scenario:filters.inflationFilter.timeSeries'),
          multiselect: false,
          options: [
            {
              name: t('scenario:filters.inflationFilter.headlineCPI'),
              key: 'headline_cpi',
              value: {
                value: '.cpi_level.change_pct(horiz=1y)',
              },
            },
            {
              name: t('scenario:filters.inflationFilter.coreCPI'),
              key: 'core_cpi',
              include: ['us', 'jpn', 'uk'],
              value: {
                value: '.cpi_core_level.change_pct(horiz=1y)',
              },
            },
            {
              name: t('scenario:filters.inflationFilter.headlinePCE'),
              key: 'headline_pce',
              include: ['us'],
              value: {
                value: '.pce_level.change_pct(horiz=1y)',
              },
            },
            {
              name: t('scenario:filters.inflationFilter.corePCE'),
              key: 'core_pce',
              include: ['us'],
              value: {
                value: '.pce_core_level.change_pct(horiz=1y)',
              },
            },
          ],
          default: 'headline_cpi',
        },
      ],
      custom: getCustomInflation(t),
    },
  },
  {
    key: 'political-events',
    name: t('scenario:filters.conditions', {
      context: 'political-events',
    }),
    visible: true,
    searchable: false,
    dynamic: true,
    config: {
      name: t('scenario:filters.politicalEventsFilter.politicalEvents'),
      multiselect: false,
      options: [
        {
          name: t(
            'scenario:filters.politicalEventsFilter.presidentialElection'
          ),
          key: 'presidential_election',
          value: {
            snake: '.presidential_election.event',
            condition: 'crosses_above',
            value: 0.5,
          },
        },
        {
          name: t(
            'scenario:filters.politicalEventsFilter.governmentShutdownStarts'
          ),
          key: 'government_shutdown_starts',
          value: {
            snake: '.government_shutdown_start',
            condition: 'crosses_above',
            value: 0.5,
          },
        },
        {
          name: t(
            'scenario:filters.politicalEventsFilter.governmentShutdownEnds'
          ),
          key: 'government_shutdown_ends',
          value: {
            snake: '.government_shutdown_end',
            condition: 'crosses_above',
            value: 0.5,
          },
        },
        {
          name: t(
            'scenario:filters.politicalEventsFilter.presidentialElectionRepublicansWin'
          ),
          key: 'presidential_election_republicans_win',
          value: {
            snake: '.event_presidential_republican_win',
            condition: 'crosses_above',
            value: 0.5,
          },
        },
        {
          name: t(
            'scenario:filters.politicalEventsFilter.presidentialElectionDemocratsWin'
          ),
          key: 'presidential_election_democrats_win',
          value: {
            snake: '.event_presidential_democratic_win',
            condition: 'crosses_above',
            value: 0.5,
          },
        },
        {
          name: t(
            'scenario:filters.politicalEventsFilter.senateElectionRepublicansWin'
          ),
          key: 'senate_election_republicans_win',
          value: {
            snake: '.event_senate_republican_win',
            condition: 'crosses_above',
            value: 0.5,
          },
        },
        {
          name: t(
            'scenario:filters.politicalEventsFilter.senateElectionDemocratsWin'
          ),
          key: 'senate_election_democrats_win',
          value: {
            snake: '.event_senate_democratic_win',
            condition: 'crosses_above',
            value: 0.5,
          },
        },
        {
          name: t(
            'scenario:filters.politicalEventsFilter.houseElectionRepublicansWin'
          ),
          key: 'house_election_republicans_win',
          value: {
            snake: '.event_house_republican_win',
            condition: 'crosses_above',
            value: 0.5,
          },
        },
        {
          name: t(
            'scenario:filters.politicalEventsFilter.houseElectionDemocratsWin'
          ),
          key: 'house_election_democrats_win',
          value: {
            snake: '.event_house_democratic_win',
            condition: 'crosses_above',
            value: 0.5,
          },
        },
      ],
      children: [
        {
          name: t('scenario:filters.politicalEventsFilter.country'),
          multiselect: false,
          options: [
            {
              name: t('scenario:filters.politicalEventsFilter.usa'),
              key: 'usa',
              value: {
                value: 'us',
              },
            },
          ],
          default: 'usa',
        },
      ],
    },
    category: 'Scenario',
    locked: true,
  },
  {
    key: 'risk-off',
    name: t('scenario:filters.conditions', {
      context: 'risk-off',
    }),
    visible: true,
    searchable: false,
    category: 'Scenario',
    locked: true,
    dynamic: true,
    config: {
      name: t('scenario:filters.riskOffFilter.riskOff'),
      multiselect: false,
      options: [
        {
          name: t('scenario:filters.riskOffFilter.sp500Drops2Day'),
          key: 'spx_500_drops_2_d',
          value: {
            snake: 'spx.price',
            condition: 'falls_by',
            value: 0.02,
            duration: 1,
            duration_unit: 'd',
          },
        },
        {
          name: t('scenario:filters.riskOffFilter.dollarIndexRises2Day'),
          key: 'dx_rises_2_d',
          value: {
            snake: 'dx_ice_fut_currency.price',
            condition: 'rises_by',
            value: 0.02,
            duration: 1,
            duration_unit: 'd',
          },
        },
        {
          name: t('scenario:filters.riskOffFilter.us10yDrop20Day'),
          key: 'us_10y_drop_20_d',
          value: {
            snake: 'sov_us_usd_10y.yield',
            condition: 'falls_by_level',
            value: 0.2,
            duration: 1,
            duration_unit: 'd',
          },
        },
      ],
    },
  },
  {
    key: 'index',
    name: t('scenario:filters.conditions', {
      context: 'index',
    }),
    visible: true,
    searchable: false,
    category: 'Scenario',
    locked: true,
    dynamic: true,
    config: {
      name: t('scenario:filters.indexFilter.index'),
      multiselect: false,
      options: [
        {
          name: t('scenario:filters.indexFilter.rises2Day'),
          key: 'rises_2_d',
          value: {
            condition: 'rises_by',
            value: 0.02,
            duration: 1,
            duration_unit: 'd',
          },
        },
        {
          name: t('scenario:filters.indexFilter.rises5Week'),
          key: 'rises_5_w',
          value: {
            condition: 'rises_by',
            value: 0.05,
            duration: 1,
            duration_unit: 'w',
          },
        },
        {
          name: t('scenario:filters.indexFilter.falls2Day'),
          key: 'falls_2_d',
          value: {
            condition: 'falls_by',
            value: 0.02,
            duration: 1,
            duration_unit: 'd',
          },
        },
        {
          name: t('scenario:filters.indexFilter.falls5Week'),
          key: 'falls_5_d',
          value: {
            condition: 'falls_by',
            value: 0.05,
            duration: 1,
            duration_unit: 'w',
          },
        },
      ],
      children: [
        {
          name: t('scenario:filters.indexFilter.instrumentSelector'),
          multiselect: false,
          options: [
            {
              name: t('scenario:filters.indexFilter.sp500'),
              key: 'spx',
              value: {
                value: 'spx.price',
              },
            },
            {
              name: t('scenario:filters.indexFilter.ftse100'),
              key: 'ftse_100',
              value: {
                value: 'ukx.price',
              },
            },
            {
              name: t('scenario:filters.indexFilter.nikkei225'),
              key: 'nikkei_225',
              value: {
                value: 'nky.price',
              },
            },
            {
              name: t('scenario:filters.indexFilter.dax'),
              key: 'dax',
              value: {
                value: 'dax.price',
              },
            },
          ],
          default: 'spx',
        },
      ],
      custom: getCustomIndexMoves(t),
    },
  },
];
